type SiteLabelSignatureProgress = 'succeeded' | 'inprogress' | 'failed';

interface ISiteLabelSignaturePosition {
    xPosition: number;
    yPosition: number;
}

class SiteLabelSignature
    extends SiteLabel<IFileIdentifier, ISiteLabelConfig> {

    private readonly _container: IQueryElement;
    private readonly _provider: string;
    private readonly _global: string;
    private readonly _input: IQueryElement;
    private readonly _canvas: SiteLabelSignatureCanvas;
    private readonly _context: CanvasRenderingContext2D;

    private _isChanged: boolean = false;
    private _value: string | null = null;
    private _drawing: boolean = false;
    private _oldPosition?: ISiteLabelSignaturePosition;
    private _newPosition?: ISiteLabelSignaturePosition;

    constructor(card: SiteCard, model: ISiteModel, modelData: ISiteModel,  element: HTMLElement) {
        super(card, model, modelData, element);

        let input = this.query("input").first();
        let provider = input && input.getAttr("data-provider");
        let global = input && input.getAttr("data-global");
        let modelValue = this.getModelValue();
        let metaData = this.getModelData();

        if (input === null || provider === null || global === null)
            throw "Couldn't find the input element.";

        this._provider = provider;
        this._global = global;

        this._input = input;
        
        // Disable upload
        this.on("click", (e) => {
            e.stop();
        });

        // Create view
        var signature = QueryElement.QueryElement("div")
            .addClass("signature");
        
        this._container = QueryElement.QueryElement("div")
            .addClass("container")
            .appendTo(signature);

        this._canvas = new SiteLabelSignatureCanvas()
            .appendTo(this._container);
        this._canvas.on("pointerdown", (e) => this.start(e));
        this._canvas.on("pointermove", (e) => this.move(e));
        this._canvas.on("pointerup", (e) => this.stop(e));

        var context = this._canvas.get2DContext();
        if (context == null)
            throw "Unable to get context";
        
        this._context = context;
        this._context.strokeStyle = "#000000";
        this._context.lineWidth = 1;

        var buttons = QueryElement.QueryElement("div")
        .addClass("buttons")
        .appendTo(signature);
    
        new SiteActionButton(card, {
            icon: "backspace",
            isFocusable: false
        })
            .appendTo(buttons)
            .on("click", () => {
                this.clearCanvas();
            });

        new SiteActionButton(card, {
            icon: "done",
            isFocusable: false
        })
            .appendTo(buttons)
            .on("click", () => {
                this.makeInactive();
            });

        this.append(signature);
    }
    init(elem: IQueryElement) {
        super.init(elem);
        let onActive = this.getCard().on("qts.active", () => {
            // Set canvas after appending
            var positionContainer = this._container.position();
            
            if (positionContainer == null)
                throw "Cannot get position of container.";

            this._canvas.setAttr("height", `${positionContainer.height}px`);
            this._canvas.setAttr("width", `${positionContainer.width}px`);
            this._context.fillStyle = "#FFFFFF";
            this._context.fillRect(0, 0, positionContainer.width, positionContainer.height);

            this.getCard().off("qts.active", onActive);
        });

    }

    start(e: Event) {
        this._drawing = true;
        this._newPosition = this._oldPosition = this.getPosition(e);
        this.drawLoop();
    }
    move(e: Event) {
        if (this._drawing)
            this._newPosition = this.getPosition(e);
    }
    stop(e: Event) {
        this._drawing = false;
        this.clearValue();
        this._isChanged = true;
    }

    drawLoop() {
        if (this._drawing) {
            window.requestAnimationFrame(() => this.drawLoop());
            this.draw();
        }
    }
    draw() {
        if (this._oldPosition == null || this._newPosition == null)
            return;
        
        this._context.beginPath();
        this._context.moveTo(this._oldPosition.xPosition, this._oldPosition.yPosition);
        this._context.lineTo(this._newPosition.xPosition, this._newPosition.yPosition);
        this._context.stroke();

        this._oldPosition = this._newPosition;
    }
    clearCanvas() {
        var positionContainer = this._container.position();
            
        if (positionContainer == null)
            throw "Cannot get position of container.";

        this._context.clearRect(0, 0, positionContainer.width, positionContainer.height);
        this._context.fillStyle = "#FFFFFF";
        this._context.fillRect(0, 0, positionContainer.width, positionContainer.height);

        this.clearValue();
    }

    getPosition(e: Event) : ISiteLabelSignaturePosition | undefined {
        let position: ISiteLabelSignaturePosition | undefined;
        let elementPosition = this._canvas.position();

        if (elementPosition == null)
            return undefined;

        if (e instanceof PointerEvent) {
            position = {
                xPosition: e.clientX - elementPosition.left,
                yPosition: e.clientY - elementPosition.top
            };
        }

        return position;
    }
    
    getInput() {
        return this._input;
    }
    defaultConfig(): ISiteLabelConfig {
        return {
            popout: 'inline',
            scroll: "top"
        }
    }
    async upload() {
        if (!this._isChanged)
            return;
        
        var blob = await this._canvas.getBlob();
        if (blob == null)
            return null;
        
        var file = new File([blob], "signature.jpg", {
            lastModified: new Date().getTime()
        });

        var fileUpload = await new QueryUploadFileUpload(file).upload(
            {
                provider: this._provider,
                global: this._global
            }
        );

        if (fileUpload == null)
            return null;
        
        this._value = fileUpload.id;
        this._isChanged = false;
    }
    clearValue() {
        this._value = null;
        this.setValue(this._value);
    }
    getValue() {
        return this._value;
    }
    setValue(value: string | null) {
        super.setValue(value);
    }
    async makeInactive() {
        await this.upload();
        super.makeInactive();
    }

    async remove(removeInner = true) {
        await super.remove(removeInner);
    }
}

class SiteLabelSignatureCanvas
    extends QueryElement<HTMLCanvasElement> {

    constructor() {
        super(document.createElement("CANVAS") as HTMLCanvasElement);
    }
    async getBlob() : Promise<Blob | null> {
        return new Promise<Blob | null>((resolve, reject) => {
            
            var element = this.get();
            if (element == null)
                return reject();
            
            element.toBlob(
                (blob) => resolve(blob), 
                'image/jpeg', 
                0.95
            );
        });
    }
    get2DContext() {
        var element = this.get();
        if (element == null)
            return null;
        
        return element.getContext("2d");
    }
}